// Copyright © 2026 Phaena Management B.V. All rights reserved.
// Proprietary and confidential. See COPYRIGHT.md in the repository root.

/**
 * Privacy — the governance story, for research leads and DPOs.
 *
 * Expands the two-walls teaser from the landing page: how the tool
 * removes the three worries people have about an analytics tool
 * touching survey data. Content mirrors the product README's
 * positioning. Static, no backend.
 */

import { Link } from "react-router-dom"
import { READER_URL } from "@/lib/links"
import { ArrowUpRight, FileCheck2, Lock, ScanSearch, ShieldCheck } from "lucide-react"

const WORRIES: { icon: typeof Lock; worry: string; answer: string }[] = [
  {
    icon: Lock,
    worry: "“Where does my data go?”",
    answer:
      "Nowhere. The Reader is a static page; your file is parsed by WebAssembly in your own browser tab and the result is a download. There is no server, no telemetry, and no API key to leak. You can confirm it in DevTools — the Network panel stays silent after the engine loads.",
  },
  {
    icon: ScanSearch,
    worry: "“What if there’s a BSN or IBAN in an answer?”",
    answer:
      "The PII gate scans every answer before you see it. Low- and medium-severity identifiers — e-mail, phone, postcode — are redacted to placeholders; BSN and IBAN are checksum-validated rather than regex-guessed, so false positives are rare and real ones are caught.",
  },
  {
    icon: FileCheck2,
    worry: "“Can I prove what happened to a DPO?”",
    answer:
      "Every run carries a SHA-256 receipt pinning the pack, its version, and the input hash: same pack + same input = same output, every time. It is the evidence that goes into a Data Processing Agreement — not a promise, an artifact.",
  },
]

export function Privacy() {
  return (
    <div className="flex flex-col gap-12 max-w-3xl">
      <header>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-4">
          <ShieldCheck className="h-4 w-4 text-primary" />
          Privacy &amp; governance
        </div>
        <h1
          className="font-display text-4xl sm:text-5xl tracking-tight text-foreground leading-tight"
          style={{ fontFamily: '"Spectral", "Iowan Old Style", Georgia, serif' }}
        >
          Local by construction.
        </h1>
        <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
          Wren is AVG/GDPR-aligned the way that actually holds up: not
          by policy, but by architecture. There is no cloud round-trip to audit,
          because there is no cloud round-trip.
        </p>
      </header>

      <section className="flex flex-col gap-6">
        {WORRIES.map(({ icon: Icon, worry, answer }) => (
          <div
            key={worry}
            className="rounded-lg border border-border bg-card p-6 flex gap-4"
          >
            <div className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-md bg-secondary text-primary">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-medium text-foreground mb-1.5">{worry}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {answer}
              </p>
            </div>
          </div>
        ))}
      </section>

      <section className="rounded-lg border border-border bg-secondary/40 p-6">
        <h2
          className="font-display text-2xl tracking-tight text-foreground mb-2"
          style={{ fontFamily: '"Spectral", Georgia, serif' }}
        >
          Not a SaaS, on purpose.
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Nothing is hosted on our side, so there are no keys to revoke and no
          breach surface to worry about. The tool is Dutch-only today — the
          lexicons, sentiment and PII detectors are tuned for Dutch survey
          language. It turns the work of reading 1,000 free answers into a
          one-screen overview; you will still want to read the top examples per
          topic.
        </p>
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <a
          href={READER_URL}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
        >
          Open the Reader
          <ArrowUpRight className="h-4 w-4" />
        </a>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-border bg-card text-foreground font-medium hover:bg-secondary/60 transition-colors"
        >
          Back to overview
        </Link>
      </div>
    </div>
  )
}
